import { useCallback, useEffect, useMemo } from 'react';
import type { GridRowSelectionModel } from '@mui/x-data-grid-pro';
import type { useDataGridApi } from 'components/common/withDataGridApi';
import { useDocumentationStore } from 'store/documentation';
import type { DocumentationViewItem } from 'store/documentation/model/types';
import { getRowId } from '../model';
import {
  createSelectedRowIdSet,
  hasSameIncludedRowSelection,
  resolveEffectiveSelectionFromGridModel,
  SELECTION_MODEL_TYPE,
} from './selectionModel';

/**
 * Row selection in both directions: store selection -> grid (after rows
 * change) and grid selection changes -> store.
 */
export function useGridRowSelection(
  rows: DocumentationViewItem[],
  apiRef: ReturnType<typeof useDataGridApi>
): { handleSelectionChange: (model: GridRowSelectionModel) => void } {
  const selectedItems = useDocumentationStore.use.selectedItems();
  const setSelectedItems = useDocumentationStore.use.setSelectedItems();

  const rowsById = useMemo(() => {
    const map = new Map<string | number, DocumentationViewItem>();
    rows.forEach((row) => map.set(getRowId(row), row));
    return map;
  }, [rows]);

  const applySelectionToGrid = useCallback(() => {
    const selectedRowIds = createSelectedRowIdSet(selectedItems);
    const currentSelectionModel = apiRef.current?.state?.rowSelection;
    if (hasSameIncludedRowSelection(currentSelectionModel, selectedRowIds)) {
      return;
    }
    apiRef.current?.setRowSelectionModel?.({
      type: SELECTION_MODEL_TYPE.INCLUDE,
      ids: selectedRowIds,
    });
  }, [selectedItems, apiRef]);

  useEffect(() => {
    if (rows.length === 0) return;

    applySelectionToGrid();
    // Again after the grid rendered the new rows.
    const rafId = requestAnimationFrame(applySelectionToGrid);
    return () => {
      cancelAnimationFrame(rafId);
    };
  }, [rows.length, selectedItems, applySelectionToGrid]);

  const handleSelectionChange = useCallback(
    (model: GridRowSelectionModel) => {
      setSelectedItems(new Set(resolveEffectiveSelectionFromGridModel(model, rowsById)));
    },
    [rowsById, setSelectedItems]
  );

  return { handleSelectionChange };
}
